/**
 * The record's own "Abrangência" row (frame `65:2`, Card/Candidatura): the campaign's electoral
 * unit and the counts `core`'s `campaign_scope.COUNTED_LEVELS` rolls up under it. Same figure
 * treatment as the creation dialog's own `LocalityStrip` (`./localityStripMessages`), which reads
 * the equivalent shape before a campaign exists -- this one reads the campaign's own settled
 * `scope`, and renders only the value side: `CampaignRecord`'s `CandidaturaRows` supplies the
 * static "Abrangência" term, the same way every other row in that list does.
 *
 * Reads through `useCampaignRecord`, the same cache entry `CampaignRecord` already holds, so
 * mounting this row costs no extra request there. Nothing renders while the record is still
 * unsettled -- no skeleton, no zero, no dash, since a transient figure a reader could mistake
 * for the real one is worse than a beat of nothing; a failed fetch renders its own error, apart
 * from "still loading". `scope` `null` (no unit chosen) also renders nothing: there is no count
 * to roll up.
 */
import type { ReactNode } from 'react';

import { ErrorState } from '../../../chrome/state';
import { useCampaignRecord } from './api/queries';
import { scopeCountLabel } from './localityStripMessages';

export function ScopeRow(props: { campaignId: string }): ReactNode {
  const query = useCampaignRecord(props.campaignId);

  if (query.status === 'pending') return null;
  if (query.status === 'error') {
    return <ErrorState failure={query.error.failure} onRetry={() => void query.refetch()} />;
  }

  const scope = query.data.scope;
  if (scope === null) return null;

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <span>{scope.name}</span>
      {scope.counts.map((count) => (
        <span key={count.level} className="inline-flex items-center gap-1">
          {/* eslint-disable-next-line no-restricted-syntax -- decorative separator, same glyph in every locale. */}
          <span aria-hidden>·</span>
          <span>{scopeCountLabel(count)}</span>
        </span>
      ))}
    </span>
  );
}
